/**
 * Pure fetch helper extracted from `useDeleteSpaceSource` so the hook itself
 * can stay focused on the React-state / sweet-state mirroring concerns and
 * the shared `useMutation` utility can drive the request lifecycle.
 *
 * The DELETE endpoint returns 204 No Content; `performHttpFetch` would
 * attempt `response.json()` and throw on an empty body, so we keep the
 * manual `fetch` here.
 */
import { FetchError } from '@atlassian/conversation-assistant-utils/httpUtils';

import { SPACES_API_PATH } from '../../common/constants';

export type DeleteSpaceSourceFetchParams = {
	spaceId: string;
	sourceId: string;
	cloudId: string;
	product: string;
	experienceId: string;
};

export const deleteSpaceSourceFetchAction = async ({
	spaceId,
	sourceId,
	cloudId,
	product,
	experienceId,
}: DeleteSpaceSourceFetchParams): Promise<void> => {
	const path = `${SPACES_API_PATH}/${spaceId}/sources/${sourceId}`;
	let response: Response;
	try {
		response = await fetch(path, {
			method: 'DELETE',
			headers: {
				'Content-Type': 'application/json',
				'x-cloudid': cloudId,
				'x-product': product,
				'x-experience-id': experienceId,
			},
			credentials: 'include',
		});
	} catch (err) {
		const originalErrorMessage = err instanceof Error ? err.message : 'Unknown error';
		throw new FetchError(
			`deleteSpaceSourceFetch error while fetching ${path}. Original error: ${originalErrorMessage}`,
			{ errorCode: 'deleteSpaceSourceFetchError' },
		);
	}
	if (!response.ok || response.status >= 400) {
		const body = await response.text().catch(() => '');
		throw new FetchError(
			`deleteSpaceSourceFetch error while fetching ${path}. Original error: [Code ${response.status}] ${body}`,
			{ errorCode: 'deleteSpaceSourceFetchError', statusCode: response.status },
		);
	}
	// 204 No Content — no body to parse.
};
